import { cloneDeep, isBoolean, isEmpty, isEqual, isNumber, isUndefined, xor } from 'lodash'

import BillingApiHandler from '@/api/billing'
import CustomFieldsApiHandler from '@/api/custom-fields'
import DataSourcesApiHandler from '@/api/datasources'
import LevelApiHandler from '@/api/level'
import PluginApiHandler from '@/api/plugin'
import {
  DELETE_USER_DATA_PARAMETER,
  SET_APP_LICENSE_BANNER_HEIGHT,
  SET_BILLING_PLAN_DETAILS,
  SET_BILLING_PLAN_DETAILS_LOADER,
  SET_EXTERNAL_ENVIRONMENT_ELEMENTS_HEIGHT,
  SET_FULL_APP_LOADER_STATUS,
  SET_FULLSCREEN,
  SET_SETTINGS_INIT_FAILED,
  SET_PAGE_HEADER_HEIGHT,
  SET_RESTRICTIONS,
  SET_SETTINGS,
  SET_SETTINGS_ARE_LOADED,
  SET_SETTINGS_PARAMETER,
  SET_SPLIT_VIEW_HEADER_STICKY_STATUS,
  SET_TOOLBAR_HEIGHT,
  SET_USER_DATA,
  SET_USER_DATA_BY_KEY,
  TOGGLE_APP_MENU_EXPANDED_STATUS,
  UPDATE_USER_SETTINGS,
  SET_PLAN_PRIORITY,
  SET_PLATFORM_API_ENABLED,
  TOGGLE_APP_MENU_EXPANDED_STATUS_FOR_HOMEPAGE
} from '@/store/mutation-types'
import { isEnterprisePlan } from '@/utils/billing-settings'
import { ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS } from '@/utils/element-progress'
import { COMPANY_SETTINGS_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { useSetAppLocale } from '@/utils/general'
import { getUserGroupsByWorkspace } from '@/utils/global-groups'
import { LICENSE_TYPES } from '@/utils/license-types'
import { PROGRESS_VIEW_COLUMN_COUNT_OPTIONS } from '@/utils/progress-view'
import { applyStatusNames } from '@/utils/status-names'
import {
  APP_LOCALE,
  APP_OPENED_COUNT,
  CUSTOM_DASHBOARD_FILTERS,
  DEFAULT_USERS_SETTINGS,
  FEEDBACK_MODAL_ID,
  getOkrElementsTableSettingsKeys,
  HOMEPAGE_OBJECTIVES_CARD_STATE,
  HOMEPAGE_SEARCH_TYPE_ID,
  HOMEPAGE_SEARCH_TYPE_ID_TOOLTIP,
  ONBOARDING_HUB,
  parseUserSettings,
  PERFORMANCE_REPORT_FILTERS,
  RELEASE_UPDATES_MODAL_ID,
  ROADMAP_ACTIVE_VIEW,
  ROADMAP_TABLE_COLLAPSED_STATE,
  ROADMAP_TABLE_SIDE_WIDTH,
  ROADMAP_VIEW_SCALE,
  SETTINGS_REFERER,
  STORYLANE,
  stringifyUserSettings,
  USER_SETTINGS_MAPPER,
  DASHBOARD_GENERAL_FILTERS,
  OKR_ELEMENT_MODAL_FULL_SCREEN_STATE,
  METRIC_UNIT_SUGGESTIONS,
  IS_SHOW_FORM_CHART,
  ELEMENT_PROGRESS_TABLE_COLUMN_COUNT,
  PROGRESS_VIEW_TABLE_COLLAPSED_STATE,
  PROGRESS_VIEW_TABLE_SIDE_WIDTH,
  PROGRESS_VIEW_GRANULATION_ID,
  IS_SHOW_BANNER_ON_FEED,
  PROGRESS_VIEW_COLUMN_COUNT,
  IS_SHOW_BANNER_ON_AUTOMATIONS_PAGE,
  APP_MENU_PROMOTION_BANNER_ID,
  HOMEPAGE_PINS,
  HOMEPAGE_ACTIVE_TAB,
  HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG,
  HOMEPAGE_AUTOMATIC_PINS
} from '@/utils/user-settings'
import { restoreLastSelectedWorkspace } from '@/utils/workspaces'
import { REQUIRED_APP_OPENED_COUNT_TO_SHOW_MODAL } from '@jira/utils/feedback-modal'

const DEFAULT_APP_LOADING_OPTIONS = {
  alpha: 0.5,
  backgroundColor: '#091e42',
  circleColor: 'var(--white-color)'
}

const PLATFORM_API_ENABLED_DEFAULT_VALUE = true

const state = {
  settings: null,
  settingsAreLoaded: false,
  settingsInitFailed: false,
  fullscreen: false,
  splitViewHeaderStickyStatus: false,
  userData: null,
  restrictions: {},
  planPriority: null,
  otWrapperHeight: 0,
  appLicenseBannerHeight: 0,
  pageHeaderHeight: 0,
  platformApiEnabled: PLATFORM_API_ENABLED_DEFAULT_VALUE,
  externalEnvironmentElementsHeight: {
    navbar: 0,
    footer: 0
  },
  fullAppLoading: {
    flag: false,
    ...DEFAULT_APP_LOADING_OPTIONS
  },
  appMenu: {
    expanded: true,
    expandedOnHomepage: false
  },
  billingPlanDetails: {
    loading: false,
    data: {
      upcomingInvoice: {}
    }
  }
}

const DASHBOARD_VISIBILITY_FALLBACK = {
  [COMPANY_SETTINGS_ENTITY_KEYS.SHOW_CUSTOM_DASHBOARD_TO_USERS]: true,
  [COMPANY_SETTINGS_ENTITY_KEYS.SHOW_GENERAL_DASHBOARD_TO_USERS]: true,
  [COMPANY_SETTINGS_ENTITY_KEYS.SHOW_PERFORMANCE_DASHBOARD_TO_USERS]: true
}

const CHECKIN_FEED_VISIBILITY_FALLBACK = true

const getters = {
  userIsAdmin: (state, getters, rootState, rootGetters) => {
    const selectedWorkspace = rootGetters['workspaces/selectedWorkspace']
    const isUserPluginAdmin = rootState.pluginOptions.isPluginAdmin

    return (
      isUserPluginAdmin ||
      (selectedWorkspace && selectedWorkspace.workspaceAdmin) ||
      state.userData?.hasAccessToWorkspaceSettingsPage ||
      state.userData?.hasAccessToGlobalGroupsPage
    )
  },

  userIsWorkspaceAdmin: state => {
    return state.userData?.hasAccessToWorkspaceSettingsPage
  },

  userRoleForTracking: (state, getters, rootState) => {
    const isUserPluginAdmin = rootState.pluginOptions.isPluginAdmin
    const isWorkspaceAdmin = state.userData?.hasAccessToWorkspaceSettingsPage

    const [role] = [
      isUserPluginAdmin && 'plugin admin',
      isWorkspaceAdmin && 'ws admin',
      'other'
    ].filter(Boolean)

    return role
  },

  showDashboardItem: (state, getters) => {
    const {
      showCustomDashboardForUsers,
      showGeneralDashboardForUsers,
      showPerformanceReportForUsers
    } = state.settings || DASHBOARD_VISIBILITY_FALLBACK

    const showDashboard = [
      showCustomDashboardForUsers,
      showGeneralDashboardForUsers,
      showPerformanceReportForUsers
    ].some(Boolean)

    return getters.userIsAdmin || showDashboard
  },

  dashboardsVisibility: (state, getters) => {
    const {
      showCustomDashboardForUsers,
      showGeneralDashboardForUsers,
      showPerformanceReportForUsers
    } = state.settings || DASHBOARD_VISIBILITY_FALLBACK

    if (getters.userIsAdmin) {
      return { ...DASHBOARD_VISIBILITY_FALLBACK }
    }

    return {
      showCustomDashboardForUsers,
      showGeneralDashboardForUsers,
      showPerformanceReportForUsers
    }
  },

  checkinFeedForUsersVisibility: (state, getters) => {
    const { showCheckinsFeedForUsers } = state.settings || CHECKIN_FEED_VISIBILITY_FALLBACK

    if (getters.userIsAdmin) {
      return CHECKIN_FEED_VISIBILITY_FALLBACK
    }

    return showCheckinsFeedForUsers
  },

  tracingDisabled: state => {
    const tracingDisabled = state.settings?.[COMPANY_SETTINGS_ENTITY_KEYS.TRACING_DISABLED]

    if (isUndefined(tracingDisabled)) {
      return true
    }

    return tracingDisabled
  },

  userGroupsByWorkspace: state => workspaceId => {
    const allGroups = state.userData?.userGroups || []

    return getUserGroupsByWorkspace({
      allGroups,
      workspaceId
    })
  },

  okrElementsTableEnabledColumns: state => tablePlacement => {
    const key = getOkrElementsTableSettingsKeys(tablePlacement).enabledColumns
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  okrElementsTableColumnsWidth: state => tablePlacement => {
    const key = getOkrElementsTableSettingsKeys(tablePlacement).columnsWidth
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  roadmapTableCollapsedState: state => {
    const key = ROADMAP_TABLE_COLLAPSED_STATE
    const valueInState = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]
    return isBoolean(valueInState) ? valueInState : DEFAULT_USERS_SETTINGS[key]
  },

  roadmapTableSideWidth: state => {
    const key = ROADMAP_TABLE_SIDE_WIDTH
    const valueInState = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]
    return isNumber(valueInState) ? valueInState : DEFAULT_USERS_SETTINGS[key]
  },

  roadmapActiveView: state => {
    const key = ROADMAP_ACTIVE_VIEW
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  roadmapViewScale: state => {
    const key = ROADMAP_VIEW_SCALE
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  progressViewTableCollapsedState: state => {
    const key = PROGRESS_VIEW_TABLE_COLLAPSED_STATE
    const valueInState = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]
    return isBoolean(valueInState) ? valueInState : DEFAULT_USERS_SETTINGS[key]
  },

  progressViewTableSideWidth: state => {
    const key = PROGRESS_VIEW_TABLE_SIDE_WIDTH
    const valueInState = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]
    return isNumber(valueInState) ? valueInState : DEFAULT_USERS_SETTINGS[key]
  },

  progressViewGranulationId: state => {
    const key = PROGRESS_VIEW_GRANULATION_ID
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  progressViewColumnCount: state => {
    const key = PROGRESS_VIEW_COLUMN_COUNT
    const savedValue =
      state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]

    if (PROGRESS_VIEW_COLUMN_COUNT_OPTIONS.includes(savedValue)) {
      return savedValue
    }

    return DEFAULT_USERS_SETTINGS[key]
  },

  releaseUpdatesModalId: state => {
    if (!state.userData) {
      return undefined
    }
    return state.userData.userSettings[USER_SETTINGS_MAPPER[RELEASE_UPDATES_MODAL_ID]] || null
  },

  feedBackModalId: state => {
    if (!state.userData) {
      return undefined
    }
    return state.userData.userSettings[USER_SETTINGS_MAPPER[FEEDBACK_MODAL_ID]] || null
  },

  appOpenedCount: state => {
    const key = APP_OPENED_COUNT
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  searchTypeId: state => {
    const key = HOMEPAGE_SEARCH_TYPE_ID
    const resolvedValue = state.userData?.userSettings[USER_SETTINGS_MAPPER[key]]
    return resolvedValue ? resolvedValue : DEFAULT_USERS_SETTINGS[key]
  },

  searchTypeIdTooltip: state => {
    const key = HOMEPAGE_SEARCH_TYPE_ID_TOOLTIP

    const resolvedValue = state.userData?.userSettings[USER_SETTINGS_MAPPER[key]]
    return isBoolean(resolvedValue) ? resolvedValue : DEFAULT_USERS_SETTINGS[key]
  },

  homepageObjectivesCardState: state => {
    const key = HOMEPAGE_OBJECTIVES_CARD_STATE
    return state.userData?.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  homepageActiveTab: state => {
    const key = HOMEPAGE_ACTIVE_TAB
    return state.userData?.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  homepageMetricsWithoutKpiCardConfig: state => {
    const key = HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG
    return state.userData?.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  performanceReportFilters: state => {
    const key = PERFORMANCE_REPORT_FILTERS
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  customDashboardFilters: state => {
    const key = CUSTOM_DASHBOARD_FILTERS
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  onboardingHubState: state => {
    const key = ONBOARDING_HUB
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  appLocale: (state, getters) => {
    const key = APP_LOCALE

    const defaultLocale = DEFAULT_USERS_SETTINGS[key]

    if (getters.isInitSettingsFailed) {
      return defaultLocale
    }

    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || defaultLocale
  },

  settingsReferer: state => {
    const key = SETTINGS_REFERER
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  isInitSettingsFailed: state => state.settingsInitFailed,

  subscriptionStatuses: state => {
    const billingPlanDetails = state.billingPlanDetails.data
    const statusId = billingPlanDetails.statusId?.toString()
    const {
      WEB_APP_TRIAL,
      WEB_APP_ACTIVE,
      WEB_APP_CANCELED,
      WEB_APP_TO_BE_CANCELED,
      WEB_APP_PAST_DUE,
      WEB_APP_OVERLIMIT,
      WEB_APP_BLOCKED
    } = LICENSE_TYPES

    return {
      isTrial: statusId === WEB_APP_TRIAL,
      isActive: statusId === WEB_APP_ACTIVE,
      isCanceled: statusId === WEB_APP_CANCELED,
      isPastDue: statusId === WEB_APP_PAST_DUE,
      toBeCanceled: statusId === WEB_APP_TO_BE_CANCELED,
      isOverLimitedUsers: statusId === WEB_APP_OVERLIMIT,
      isBlocked: statusId === WEB_APP_BLOCKED,
      isEnterprise: isEnterprisePlan(billingPlanDetails?.name),
      isPlanNone: billingPlanDetails?.name === 'None'
    }
  },

  storylaneSettings: state => {
    const key = STORYLANE
    return (
      state.userData?.userSettings[USER_SETTINGS_MAPPER[key]] || {
        ...DEFAULT_USERS_SETTINGS[key],
        buttonId: DEFAULT_USERS_SETTINGS[key].buttonId - 1 // только для первого показа после релиза
      }
    )
  },

  dashboardGeneralFiltersByWorkspaceId:
    state =>
    ({ workspaceId }) => {
      const key = DASHBOARD_GENERAL_FILTERS
      const DEFAULT_VALUE = cloneDeep(DEFAULT_USERS_SETTINGS[key])

      if (!workspaceId) {
        return DEFAULT_VALUE
      }

      const valueFromState = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]

      if (!valueFromState) {
        return DEFAULT_VALUE
      }

      return valueFromState[workspaceId] || DEFAULT_VALUE
    },

  dashboardGeneralFilters: state => {
    const key = DASHBOARD_GENERAL_FILTERS
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  okrElementModalFullscreenState: state => {
    const key = OKR_ELEMENT_MODAL_FULL_SCREEN_STATE
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  metricUnitSuggestions: state => {
    const key = METRIC_UNIT_SUGGESTIONS
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]
  },

  isShowProgressChart: state => {
    const key = IS_SHOW_FORM_CHART
    const savedValue = state.userData.userSettings[USER_SETTINGS_MAPPER[key]]

    if (isUndefined(savedValue)) {
      return DEFAULT_USERS_SETTINGS[key]
    }

    return savedValue
  },

  savedColumnCount: state => {
    const key = ELEMENT_PROGRESS_TABLE_COLUMN_COUNT
    const savedValue =
      state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]

    if (ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS.includes(savedValue)) {
      return savedValue
    }

    return DEFAULT_USERS_SETTINGS[key]
  },

  isShowBannerOnFeed: state => {
    const key = IS_SHOW_BANNER_ON_FEED
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] ?? DEFAULT_USERS_SETTINGS[key]
  },

  isShowBannerOnAutomationsPage: state => {
    const key = IS_SHOW_BANNER_ON_AUTOMATIONS_PAGE
    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] ?? DEFAULT_USERS_SETTINGS[key]
  },

  isShowAppMenuPromotionBanner: state => {
    const key = APP_MENU_PROMOTION_BANNER_ID
    return (
      (state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || null) < DEFAULT_USERS_SETTINGS[key]
    )
  },

  homepagePinnedGroups: state => {
    const key = HOMEPAGE_PINS

    const savedValue =
      state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]

    return savedValue.groups
  },

  homepagePinnedUsers: state => {
    const key = HOMEPAGE_PINS

    const savedValue =
      state.userData.userSettings[USER_SETTINGS_MAPPER[key]] || DEFAULT_USERS_SETTINGS[key]

    return savedValue.users
  },

  isGroupPinned:
    (state, getters) =>
    ({ groupId }) => {
      return getters.homepagePinnedGroups.includes(groupId)
    },

  isUserPinned:
    (state, getters) =>
    ({ accountId }) => {
      return getters.homepagePinnedUsers.includes(accountId)
    },

  homepageAutoPinStatus: state => {
    const key = HOMEPAGE_AUTOMATIC_PINS

    return state.userData.userSettings[USER_SETTINGS_MAPPER[key]] ?? DEFAULT_USERS_SETTINGS[key]
  },

  blockExploringOtherPeopleProgressOnHomepage: (state, getters) => {
    if (getters.userIsAdmin) return false

    return (
      state.settings?.[COMPANY_SETTINGS_ENTITY_KEYS.HIDE_OTHER_USERS_AND_GROUPS_ON_HOMEPAGE] ??
      false
    )
  }
}

const mutations = {
  [SET_USER_DATA](state, userData) {
    state.userData = userData
  },

  [SET_USER_DATA_BY_KEY](state, { key, value }) {
    state.userData[key] = value
  },

  [DELETE_USER_DATA_PARAMETER](state, parameter) {
    delete state.userData[parameter]
  },

  [SET_SETTINGS](state, settings) {
    state.settings = settings
  },

  [SET_SETTINGS_ARE_LOADED](state, value) {
    state.settingsAreLoaded = value
  },

  [SET_FULLSCREEN](state, flag) {
    state.fullscreen = flag
  },

  [SET_SPLIT_VIEW_HEADER_STICKY_STATUS](state, flag) {
    state.splitViewHeaderStickyStatus = flag
  },

  [SET_SETTINGS_PARAMETER](state, { parameter, value }) {
    if (!state.settings) {
      state.settings = {}
    }
    state.settings[parameter] = value
  },

  [TOGGLE_APP_MENU_EXPANDED_STATUS]: (state, status) => {
    state.appMenu.expanded = status
  },

  [TOGGLE_APP_MENU_EXPANDED_STATUS_FOR_HOMEPAGE]: state => {
    state.appMenu.expandedOnHomepage = !state.appMenu.expandedOnHomepage
  },

  [SET_TOOLBAR_HEIGHT]: (state, height) => {
    state.otWrapperHeight = height
  },

  [SET_PAGE_HEADER_HEIGHT]: (state, height) => {
    state.pageHeaderHeight = height
  },

  [SET_FULL_APP_LOADER_STATUS]: (state, payload) => {
    state.fullAppLoading = payload
  },

  [SET_APP_LICENSE_BANNER_HEIGHT]: (state, height) => {
    state.appLicenseBannerHeight = height
  },

  [SET_EXTERNAL_ENVIRONMENT_ELEMENTS_HEIGHT]: (state, values = { navbar: 0, footer: 0 }) => {
    state.externalEnvironmentElementsHeight = values
  },

  [UPDATE_USER_SETTINGS](state, data) {
    state.userData.userSettings = {
      ...state.userData.userSettings,
      ...data
    }
  },

  [SET_RESTRICTIONS](state, restrictions) {
    state.restrictions = restrictions
  },

  [SET_PLAN_PRIORITY](state, planPriority) {
    state.planPriority = planPriority
  },

  [SET_BILLING_PLAN_DETAILS](state, data) {
    state.billingPlanDetails.data = data
  },

  [SET_BILLING_PLAN_DETAILS_LOADER](state, payload) {
    state.billingPlanDetails.loading = payload
  },

  [SET_SETTINGS_INIT_FAILED](state, value) {
    state.settingsInitFailed = value
  },

  [SET_PLATFORM_API_ENABLED](state, value) {
    state.platformApiEnabled = value ?? PLATFORM_API_ENABLED_DEFAULT_VALUE
  }
}

const actions = {
  //async requestImageCookies({ rootGetters, rootState }) {
  //  const isJiraApp = rootGetters['appContext/isBuiltInJira']
  //  const isConfluenceApp = rootGetters['appContext/isBuiltInConfluence']
  //
  //  const imageCookiesApi = new ImageCookiesApiHandler()
  //
  //  try {
  //    await imageCookiesApi.setCookiesForImages({
  //      isJiraApp,
  //      isConfluenceApp,
  //      pluginType: rootState.pluginOptions[PLUGIN_OPTIONS_KEYS.PLUGIN_TYPE]
  //    })
  //  } catch (error) {
  //    handleError({ error })
  //  }
  //},

  async fetchInitialWorkspaces({ rootState, dispatch }) {
    const pluginApi = new PluginApiHandler()
    const isUserPluginAdmin = rootState.pluginOptions.isPluginAdmin

    const initialValues = await pluginApi.getInitValues({
      filterWorkspacesWhereUserDisabled: !isUserPluginAdmin
    })

    const { userWorkspaces } = initialValues

    dispatch('workspaces/setWorkspaces', userWorkspaces, { root: true })
    // dispatch('requestImageCookies')
  },

  async fetchUserData({ state, commit, rootState, dispatch }) {
    if (!isEmpty(state.userData)) {
      return
    }
    commit(SET_SETTINGS_INIT_FAILED, false)

    const pluginApi = new PluginApiHandler()
    const isUserPluginAdmin = rootState.pluginOptions.isPluginAdmin

    const initialValues = await pluginApi.getInitValues({
      filterWorkspacesWhereUserDisabled: !isUserPluginAdmin
    })

    const {
      userAccountId,
      userAvatarUrl,
      userGroups,
      email,
      hasAccessToWorkspaceSettingsPage,
      hasAccessToGlobalGroupsPage,
      userName,
      organizations,
      userSettings
    } = initialValues

    commit(SET_USER_DATA, {
      userName,
      userAccountId,
      userAvatarUrl,
      userGroups,
      email,
      hasAccessToWorkspaceSettingsPage,
      hasAccessToGlobalGroupsPage,
      userSettings
    })

    const isWebApp = rootState.appContext.isWebApp
    const isCrossPlatformApp = rootState.appContext.isCrossPlatformApp

    if (isWebApp || isCrossPlatformApp) {
      dispatch('appContext/setOrganizations', organizations, { root: true })
    }

    // dispatch('requestImageCookies')
  },

  async fetchInitData({ commit, dispatch, state, rootState }, { initialWorkspaceId }) {
    if (state.settingsAreLoaded && state.settings) {
      return
    }
    const isUserPluginAdmin = rootState.pluginOptions.isPluginAdmin

    const pluginApi = new PluginApiHandler()
    const customFieldsApi = new CustomFieldsApiHandler()
    const levelsApi = new LevelApiHandler()

    let data = null

    try {
      commit(SET_SETTINGS_INIT_FAILED, false)
      const [initialValues, customFields, fullLevelsList] = await Promise.all([
        pluginApi.getInitValues({
          filterWorkspacesWhereUserDisabled: !isUserPluginAdmin
        }),
        customFieldsApi.getCustomFieldsData(),
        levelsApi.getLevels()
        // dispatch('requestImageCookies')
      ])

      data = initialValues

      const userSettings = parseUserSettings(data.userSettings)

      const { confidenceLevelCustomNames, ...rest } = data.settings

      applyStatusNames({ confidenceLevelCustomNames })

      commit(SET_SETTINGS, rest)
      const {
        userAccountId,
        userAvatarUrl,
        userGroups,
        email,
        hasAccessToWorkspaceSettingsPage,
        hasAccessToGlobalGroupsPage,
        hasAccessToJira,
        userName,
        organizations,
        restrictions,
        userDataSources,
        planPriority,
        platformApiEnabled
      } = data

      commit(SET_USER_DATA, {
        userName,
        userAccountId,
        userAvatarUrl,
        userGroups,
        email,
        userSettings,
        hasAccessToWorkspaceSettingsPage,
        hasAccessToGlobalGroupsPage,
        hasAccessToJira,
        userDataSources
      })

      const appLocale =
        state.userData.userSettings[USER_SETTINGS_MAPPER[APP_LOCALE]] ||
        DEFAULT_USERS_SETTINGS[APP_LOCALE]
      useSetAppLocale(appLocale)

      if (restrictions) {
        commit(SET_RESTRICTIONS, restrictions)
      }

      commit(SET_PLAN_PRIORITY, planPriority ?? null)

      commit(SET_PLATFORM_API_ENABLED, platformApiEnabled)

      const isWebApp = rootState.appContext.isWebApp
      const isCrossPlatformApp = rootState.appContext.isCrossPlatformApp
      if (isWebApp || isCrossPlatformApp) {
        dispatch('appContext/setOrganizations', organizations, { root: true })
      }

      dispatch('customFields/setCustomFields', customFields, { root: true })
      dispatch('objectives/setFullLevelsList', fullLevelsList, { root: true })
      commit(SET_SETTINGS_ARE_LOADED, true)
    } catch (error) {
      console.error(error)
      commit(SET_SETTINGS_INIT_FAILED, true)
      return
    }

    dispatch('workspaces/setWorkspaces', data.userWorkspaces, { root: true })

    const workspaceIdAvailable = workspaceId => {
      return data.userWorkspaces.some(ws => ws.id === workspaceId)
    }

    let workspaceId = null
    if (initialWorkspaceId && workspaceIdAvailable(initialWorkspaceId)) {
      workspaceId = initialWorkspaceId
    } else {
      workspaceId = restoreLastSelectedWorkspace(data.userWorkspaces)
    }

    if (workspaceId !== null) {
      dispatch('workspaces/setWorkspaceId', workspaceId, { root: true })
    }

    dispatch('workspaces/setWorkspacesAreLoaded', true, { root: true })
  },

  updateUserName({ commit }, value) {
    commit(SET_USER_DATA_BY_KEY, {
      key: 'userName',
      value
    })
  },

  toggleFullscreen({ commit, state }, flag) {
    const resolvedFlag = flag !== undefined ? flag : !state.fullscreen
    commit(SET_FULLSCREEN, resolvedFlag)
  },

  toggleSplitViewHeaderStickyStatus({ commit, state }, flag) {
    const resolvedFlag = flag !== undefined ? flag : !state.splitViewHeaderStickyStatus
    commit(SET_SPLIT_VIEW_HEADER_STICKY_STATUS, resolvedFlag)
  },

  toggleFullAppLoader(
    { commit, state },
    { flag = undefined, ...payload } = { flag: undefined, ...DEFAULT_APP_LOADING_OPTIONS }
  ) {
    const resolvedFlag = flag !== undefined ? flag : !state.fullAppLoading.flag
    commit(SET_FULL_APP_LOADER_STATUS, { ...state.fullAppLoading, flag: resolvedFlag, ...payload })
  },

  toggleMenuExpandedStatus({ commit }, status) {
    commit(TOGGLE_APP_MENU_EXPANDED_STATUS, Boolean(status))
  },

  toggleMenuExpandedStatusForHomepage({ commit }) {
    commit(TOGGLE_APP_MENU_EXPANDED_STATUS_FOR_HOMEPAGE)
  },

  setSettingsParameter({ commit }, { parameter, value }) {
    commit(SET_SETTINGS_PARAMETER, { parameter, value })
  },

  deleteUserDataParameter({ commit }, parameter) {
    commit(DELETE_USER_DATA_PARAMETER, parameter)
  },

  setOtWrapperHeight({ state, commit }, height) {
    if (state.otWrapperHeight !== height) {
      commit(SET_TOOLBAR_HEIGHT, height)
    }
  },

  setPageHeaderHeight({ state, commit }, height) {
    if (state.pageHeaderHeight !== height) {
      commit(SET_PAGE_HEADER_HEIGHT, height)
    }
  },

  setAppLicenseBannerHeight({ state, commit }, height) {
    if (state.appLicenseBannerHeight !== height) {
      commit(SET_APP_LICENSE_BANNER_HEIGHT, height)
    }
  },

  setExternalEnvironmentElementsHeight({ state, commit }, values) {
    if (!isEqual(state.externalEnvironmentElementsHeight, values)) {
      commit(SET_EXTERNAL_ENVIRONMENT_ELEMENTS_HEIGHT, values)
    }
  },

  async updateUserSettings({ commit }, updatedUserSettings) {
    try {
      const pluginApi = new PluginApiHandler()
      const payload = stringifyUserSettings(updatedUserSettings)
      const { settings } = await pluginApi.updateUserSettings(payload)
      commit(UPDATE_USER_SETTINGS, parseUserSettings(settings))
      return true
    } catch (error) {
      handleError({ error })
      return false
    }
  },

  async togglePinStatusForGroup({ dispatch, getters }, { groupId }) {
    await dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_PINS]]: {
        users: getters.homepagePinnedUsers,
        groups: xor(getters.homepagePinnedGroups, [groupId])
      }
    })
  },

  async togglePinStatusForUser({ dispatch, getters }, { accountId }) {
    await dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_PINS]]: {
        users: xor(getters.homepagePinnedUsers, [accountId]),
        groups: getters.homepagePinnedGroups
      }
    })
  },

  async markAutoPinPerformed({ dispatch }) {
    await dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_AUTOMATIC_PINS]]: true
    })
  },

  async applyAutoPins({ dispatch }, { users = [], groups = [] }) {
    await dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_AUTOMATIC_PINS]]: true,
      [USER_SETTINGS_MAPPER[HOMEPAGE_PINS]]: {
        users,
        groups
      }
    })
  },

  async updatePinnedGroupsOrder({ dispatch, getters }, { groupIds }) {
    return dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_PINS]]: {
        users: getters.homepagePinnedUsers,
        groups: groupIds
      }
    })
  },

  async updatePinnedUsersOrder({ dispatch, getters }, { accountIds }) {
    return dispatch('updateUserSettings', {
      [USER_SETTINGS_MAPPER[HOMEPAGE_PINS]]: {
        users: accountIds,
        groups: getters.homepagePinnedGroups
      }
    })
  },

  async fetchBillingPlanDetails({ commit }, { withLoading = true } = {}) {
    const BillingApi = new BillingApiHandler()

    if (withLoading) {
      commit(SET_BILLING_PLAN_DETAILS_LOADER, true)
    }
    const data = await BillingApi.getBillingPlanDetails()

    commit(SET_BILLING_PLAN_DETAILS, data)
    commit(SET_BILLING_PLAN_DETAILS_LOADER, false)
  },

  async increaseAppOpenedCount({ dispatch, state }) {
    const openedCount = state.userData.userSettings?.[USER_SETTINGS_MAPPER[APP_OPENED_COUNT]] || 0
    if (openedCount < REQUIRED_APP_OPENED_COUNT_TO_SHOW_MODAL) {
      await dispatch('updateUserSettings', {
        [USER_SETTINGS_MAPPER[APP_OPENED_COUNT]]: openedCount + 1
      })
    }
  },

  async fetchUserDataSources({ commit }) {
    const dataSourcesApi = new DataSourcesApiHandler()
    const dataSources = await dataSourcesApi.getDataSources()
    commit(SET_USER_DATA_BY_KEY, {
      key: 'userDataSources',
      value: dataSources.userDataSources ?? []
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
